// Explicit .ts extensions: this module is unit-tested by Node directly, and
// Node's type stripping requires them.
import { nmFrom } from './stations.ts';
import type { Station } from './types.ts';

/**
 * Tidal current stations, taken from the set the San Francisco Bar Pilots publish
 * for working the Bay: https://sfbarpilots.com/partners/noaa-tide-current-links/
 *
 * This is a curated list, which is exactly its value — NOAA exposes 261 current
 * prediction points inside the Bay, most of them subordinate stations offset from
 * a reference. These 14 are the ones professionals actually steer by.
 *
 * The `_NN` suffix on the pilots' links (`SFB1203_18`) is a depth bin in NOAA's
 * web viewer, not part of the API station id — passing it verbatim is rejected.
 * The bare id already resolves to the same bin the pilots use: `SFB1203` returns
 * bin 18 at 30 ft, matching their "(30D)".
 *
 * Every station here must be **harmonic** (`type=H` in the metadata API).
 * Subordinate stations are published as offsets from a reference and return only
 * a handful of slack and maximum events instead of a continuous series — asking
 * one for `interval=30` yields 8 points where a harmonic station yields 48, which
 * would draw a jagged, meaningless curve. Alcatraz has four current stations and
 * only two of them qualify.
 */
export interface CurrentStation {
  id: string;
  name: string;
  lat: number;
  lon: number;
}

export const CURRENT_STATIONS: CurrentStation[] = [
  { id: 'SFB1203', name: 'Golden Gate Bridge, 0.46nm E', lat: 37.8201, lon: -122.473 },
  { id: 'SFB1205', name: 'North Point, Pier 35', lat: 37.8142, lon: -122.407 },
  { id: 'SFB1206', name: 'Pier 23', lat: 37.8053, lon: -122.3973 },
  { id: 'SFB1217', name: 'Rincon Point', lat: 37.7903, lon: -122.3734 },
  // Alcatraz: the stream splits around the island, so the two sides genuinely
  // differ — the ebb sets 271° to the southwest and 293° to the north.
  { id: 'SFB1204', name: 'Alcatraz, southwest of', lat: 37.8143, lon: -122.432 },
  { id: 'SFB1211', name: 'Alcatraz, 0.5nm north of', lat: 37.8351, lon: -122.4201 },
  { id: 'PCT0336', name: 'Oakland, Yerba Buena Island', lat: 37.8098, lon: -122.3507 },
  { id: 'SFB1308', name: 'Hunters Point, 1.6nm SE', lat: 37.696, lon: -122.3381 },
  { id: 'SFB1305', name: 'San Mateo Bridge', lat: 37.5878, lon: -122.2502 },
  { id: 'PCT0666', name: 'Richmond', lat: 37.9293, lon: -122.425 },
  { id: 'SFB1310', name: 'Point Chauncey, 1.25nm N', lat: 37.9153, lon: -122.4461 },
  { id: 'SFB1316', name: 'Davis Point, 1.0nm NW', lat: 38.0619, lon: -122.2767 },
  { id: 'SFB1319', name: 'Carquinez Strait', lat: 38.0614, lon: -122.2182 },
  { id: 'SFB1317', name: 'Mare Island Strait', lat: 38.0772, lon: -122.2456 },
  { id: 's06010', name: 'Benicia Bridge', lat: 38.0346, lon: -122.1252 },
  { id: 'SFB1326', name: 'New York Slough', lat: 38.0325, lon: -121.8691 },
];

/** The reference everyone knows, used for the Bay-wide context strip. */
export const GATE_STATION = CURRENT_STATIONS[0];

/**
 * How far a current prediction may be carried from its station. The Bay's
 * streams turn hard around every point and shoal, so this stays tight.
 */
export const MAX_TRANSFER_NM = 5;

/**
 * The current station governing a wind station, or null when none is close
 * enough to speak for it.
 *
 * This exists because using the Golden Gate everywhere was simply wrong. The ebb
 * does not leave the Bay on one bearing — it sets 257° at the Gate, 338° off
 * Yerba Buena, and 147° at Richmond. Against a WSW sea breeze that is the
 * difference between a steep chop and a harmless cross-set, and the Gate
 * extrapolation got the verdict wrong at 7 of the 8 stations it covered.
 */
export function currentStationFor(station: Station): CurrentStation | null {
  let best: CurrentStation | null = null;
  let bestNm = Infinity;

  for (const c of CURRENT_STATIONS) {
    const d = nmFrom(station.lat, station.lon, { lat: c.lat, lon: c.lon });
    if (d < bestNm) {
      bestNm = d;
      best = c;
    }
  }

  return bestNm <= MAX_TRANSFER_NM ? best : null;
}
